import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Result, Button } from 'antd';
import { useAuth, useAuthActions } from '../../hooks/useAuthStore';
import { Permission, AdminRole } from '@shared/types/auth';
import { hasPermission, hasAnyRole } from '@shared/utils';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredPermissions?: Permission[];
  requiredRoles?: AdminRole[];
  fallback?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredPermissions = [],
  requiredRoles = [],
  fallback
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { checkAuth } = useAuthActions();
  const location = useLocation();

  // Check authentication on mount
  useEffect(() => {
    if (requireAuth && !isAuthenticated && !isLoading) {
      checkAuth();
    }
  }, [requireAuth, isAuthenticated, isLoading, checkAuth]);

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#666' }}>
          Kimlik doğrulanıyor...
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check role requirements
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = hasAnyRole(user.role, requiredRoles);
    
    if (!hasRequiredRole) {
      return (
        <Result
          status="403"
          title="403 - Yetkisiz Erişim"
          subTitle="Bu sayfaya erişim için gerekli yetkiye sahip değilsiniz."
          extra={
            <Button type="primary" onClick={() => window.history.back()}>
              Geri Dön
            </Button>
          }
        />
      );
    }
  }

  // Check permission requirements
  if (requiredPermissions.length > 0 && user) {
    const userPermissions = user.permissions || [];
    const hasAllRequiredPermissions = requiredPermissions.every(permission =>
      hasPermission(userPermissions, permission)
    );
    
    if (!hasAllRequiredPermissions) {
      return (
        <Result
          status="403"
          title="403 - Yetkisiz Erişim"
          subTitle="Bu işlem için gerekli izinlere sahip değilsiniz."
          extra={
            <div>
              <p><strong>Gerekli İzinler:</strong></p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                {requiredPermissions.map(permission => (
                  <li key={permission}>{permission}</li>
                ))}
              </ul>
              <br />
              <Button type="primary" onClick={() => window.history.back()}>
                Geri Dön
              </Button>
            </div>
          }
        />
      );
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default ProtectedRoute;
